import React, { CSSProperties, MutableRefObject, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';

import {
  ArrowBack,
  ExpandLess,
  ExpandMore,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
} from '@mui/icons-material';

import {
  IDocumentParagraph,
  IDocumentSection,
  IViewDocument,
  IFormData,
  IDocumentInput,
} from '@homeporter/types';

import ViewPDFIcon from '../../icons/ViewPDFIcon';
import PDFViewModal from '../PDFViewModal';
import { useKnowledgeBaseStyles } from './styles';
import FormView from './FormView';

const ChecklistView = ({ data }: { data?: IFormData[] | null }) => {
  if (!data) return null;

  return (
    <>
      {data.map((sectionEntry, sectionIndex) => (
        <Box
          sx={{ paddingLeft: '1rem' }}
          key={sectionEntry.title || sectionIndex}>
          <h3 style={{ margin: '.5rem 0' }}>{sectionEntry.title}</h3>
          {sectionEntry.children.map((child, childIndex) => (
            <Box
              sx={{ paddingLeft: '1rem' }}
              key={child.subtitle || childIndex}>
              <h4 style={{ margin: '.5rem 0' }}>{child.subtitle}</h4>
              {child.items.map((item: IDocumentInput, idx: number) => (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '2rem',
                  }}
                  key={idx}>
                  <Typography style={{ margin: 0 }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      ))}
    </>
  );
};

const renderNestedSectionsRecursive = (
  sections: IDocumentSection[],
  viewDocument: IViewDocument,
  setViewDocument: (viewDocument: IViewDocument) => void,
  //setViewedSection: (sectionSelected: IViewDocument) => void,
  styles: { sectionTitle: CSSProperties },
  level = 0,
) => {
  const handleSectionClick = (viewSection: number) => {
    if (!viewDocument) return;

    console.log(
      'select section, viewSection: ' + viewSection,
      viewDocument.sections && viewDocument.sections[viewSection],
    );

    if (viewDocument.sections && !viewDocument.sections[viewSection].pidx)
      ++viewSection;

    setViewDocument({ ...viewDocument, viewSection });
    //setViewedSection({ ...viewDocument, viewSection });
  };

  const handleToggleExpanded = (
    item: IDocumentSection,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    console.log('handleToggleExpanded ', item);
    item.expanded = !item?.expanded;
    if (viewDocument) setViewDocument({ ...viewDocument });
  };

  return sections.map((item) => (
    <Box key={item.section}>
      <Typography
        sx={{
          ...styles.sectionTitle,
          paddingLeft: `${level}rem`,
        }}
        onClick={() => handleSectionClick(item.idx)}>
        {`${item.section} ${item.title}`}

        {item.children?.length ? (
          <Box sx={{ marginLeft: 'auto' }}>
            <IconButton
              sx={{ padding: 0 }}
              onClick={(e: any) => handleToggleExpanded(item, e)}>
              {item.expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        ) : undefined}
      </Typography>

      {item.expanded && item.children?.length && (
        <Box>
          {renderNestedSectionsRecursive(
            item.children,
            viewDocument,
            setViewDocument,
            styles,
            level + 1,
          )}
        </Box>
      )}
    </Box>
  ));
};

interface DocumentViewProps {
  clearSearchResults: () => void;
  handleBackButton: () => void;
  viewDocument: IViewDocument;
  setViewDocument: (viewDocument: IViewDocument) => void;
  documentParagraphMapRef: MutableRefObject<Map<
    string,
    IDocumentParagraph
  > | null>;
  searchResults: any;
  documentLoading: boolean;
}

const DocumentView = ({
  clearSearchResults,
  handleBackButton,
  viewDocument,
  setViewDocument,
  documentParagraphMapRef,
  searchResults,
  documentLoading,
}: DocumentViewProps) => {
  const styles = useKnowledgeBaseStyles();
  const [openPDFModal, setOpenPDFModal] = useState(false);

  let lastSection = '';

  return (
    <>
      <Box sx={styles.back}>
        <Typography fontSize={'1rem'} fontWeight={500}>
          {viewDocument?.title ? viewDocument?.title : 'Document Sections'}
        </Typography>

        <Box display={'flex'} alignItems={'center'}>
          {viewDocument?.viewSection && (
            <>
              <KeyboardArrowLeftOutlined
                onClick={() =>
                  viewDocument?.viewSection &&
                  viewDocument.viewSection > 0 &&
                  setViewDocument({
                    ...viewDocument,
                    viewSection: viewDocument.viewSection - 1,
                  })
                }
                sx={styles.srbutton}
              />
              <KeyboardArrowRightOutlined
                onClick={() =>
                  viewDocument?.viewSection &&
                  setViewDocument({
                    ...viewDocument,
                    viewSection: viewDocument.viewSection + 1,
                  })
                }
                sx={styles.srbutton}
              />
            </>
          )}
          {!searchResults && (
            <IconButton
              sx={{
                width: 'auto',
                height: 'auto',
              }}
              size="medium"
              onClick={() => setOpenPDFModal(true)}>
              <ViewPDFIcon />
            </IconButton>
          )}
          <Button
            onClick={() => {
              if (searchResults) {
                clearSearchResults();
              }
              handleBackButton();
            }}>
            <Typography sx={styles.back_arrow}>
              <ArrowBack fontSize="small" /> Back
            </Typography>
          </Button>
        </Box>
      </Box>

      {documentLoading && (
        <Box sx={styles.center}>
          <CircularProgress />
        </Box>
      )}

      {viewDocument?.viewSection ? (
        <Box sx={styles.content}>
          {viewDocument?.paragraphs &&
            viewDocument.paragraphs.map((pid, index) => {
              const paragraph = documentParagraphMapRef.current?.get(pid);
              if (!paragraph || !paragraph.ref.section) return <></>;

              let title = '';
              if (paragraph.ref.section != lastSection) {
                const section = viewDocument?.sections?.find(
                  (item) => item.section === paragraph.ref.section,
                );

                if (section) title = `${section.section} ${section.title}`;
                lastSection = paragraph.ref.section;
              }

              const images = paragraph.ref.illustrations.map(
                (iid: string, idx: number) => (
                  <img
                    key={`illustration-${index}-${idx}`}
                    style={styles.img}
                    src={`${window._env_?.LIBRARY_BASE_URL || process.env.REACT_APP_LIBRARY_BASE_URL}/${viewDocument.key}/img/${iid}.png`}
                    alt={`Illustration ${iid}`}
                    loading="lazy"
                  />
                ),
              );

              return (
                <>
                  {!!title && (
                    <Typography
                      sx={styles.stitle}
                      key={`title-${index}`}>{`${title}`}</Typography>
                  )}
                  {!!paragraph.ref.subtitle && (
                    <Typography
                      sx={styles.subtitle}
                      key={`subtitle-${index}`}>{`${paragraph.ref.subtitle}`}</Typography>
                  )}
                  <Typography
                    sx={{
                      ...styles.p,
                      backgroundColor:
                        viewDocument?.searchResult === paragraph.idx
                          ? '#ffe'
                          : 'transparent',
                    }}
                    key={`paragraph-${index}`}>{`${paragraph.text}`}</Typography>
                  {images}
                </>
              );
            })}
        </Box>
      ) : viewDocument?.type === 'BOOK' ? (
        <Box sx={styles.sections}>
          {viewDocument?.nestedSections &&
            renderNestedSectionsRecursive(
              viewDocument.nestedSections,
              viewDocument,
              setViewDocument,
              //setViewedSection,
              styles,
            )}
        </Box>
      ) : viewDocument?.type === 'CHECKLIST' ? (
        <Box sx={styles.sections}>
          <ChecklistView data={viewDocument.data} />
        </Box>
      ) : (
        viewDocument?.type === 'FORM' && (
          <Box sx={styles.sections}>
            <FormView data={viewDocument.data} />
          </Box>
        )
      )}

      {viewDocument && (
        <PDFViewModal
          openModal={openPDFModal}
          closeModal={() => setOpenPDFModal(false)}
          pdf={viewDocument}
        />
      )}
    </>
  );
};

export default DocumentView;
